/**
 * Picks the snap a drag gesture on a bottom sheet lands on.
 *
 * A flick wins: the sheet carries on in the flick direction from wherever the
 * finger released it, as far as its speed takes it — a soft flick reaches the
 * next snap, a hard one skips over the snaps in between. Otherwise a drag past
 * the commit distance goes to whichever snap in the travel direction is
 * closest to the release point — a long pull crosses several snaps in one
 * gesture — and a shorter drag springs back to the snap it started from.
 */

export interface Snap<T> {
  key: T;
  height: number;
}

export interface SnapGesture<T> {
  snaps: Snap<T>[];
  start: Snap<T>;
  /** Sheet height when the finger lifted. */
  endHeight: number;
  /** In px/ms along the screen's y axis: negative when the finger moves up, i.e. the sheet grows. */
  velocity: number;
}

// A flick, in px/ms: below it the gesture is judged on the distance travelled.
export const FLICK_VELOCITY = 0.25;
// How long the sheet is imagined to keep the finger's speed once released: the
// distance it would cover in that time decides how many snaps the flick skips.
const FLICK_PROJECTION_MS = 400;
// Distance that commits the sheet to another snap: a fraction of the smallest
// gap between two snaps, capped so a tall screen doesn't ask for a long drag.
const COMMIT_RATIO = 0.2;
const MAX_COMMIT_DISTANCE = 56;

export function resolveSnap<T>({ snaps, start, endHeight, velocity }: SnapGesture<T>): T {
  if (Math.abs(velocity) > FLICK_VELOCITY) {
    const beyond = snapsBeyond(snaps, endHeight, velocity < 0);
    if (!beyond.length) return nearest(snaps, endHeight).key;
    // The finger moving up (negative velocity) grows the sheet, hence the sign.
    const projectedHeight = endHeight - velocity * FLICK_PROJECTION_MS;
    return nearest(beyond, projectedHeight).key;
  }

  const travelled = endHeight - start.height;
  if (Math.abs(travelled) <= commitDistance(snaps)) return start.key;

  const beyond = snapsBeyond(snaps, start.height, travelled > 0);
  return beyond.length ? nearest(beyond, endHeight).key : start.key;
}

function snapsBeyond<T>(snaps: Snap<T>[], height: number, above: boolean): Snap<T>[] {
  return snaps.filter(snap => (above ? snap.height > height : snap.height < height));
}

function nearest<T>(snaps: Snap<T>[], height: number): Snap<T> {
  return snaps.reduce((best, snap) =>
    Math.abs(snap.height - height) < Math.abs(best.height - height) ? snap : best,
  );
}

function commitDistance<T>(snaps: Snap<T>[]): number {
  const heights = [...new Set(snaps.map(snap => snap.height))].sort((a, b) => a - b);
  let minGap = Infinity;
  for (let i = 1; i < heights.length; i++) minGap = Math.min(minGap, heights[i] - heights[i - 1]);
  return Math.min(minGap * COMMIT_RATIO, MAX_COMMIT_DISTANCE);
}
